import { useState, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { usePermissions } from "@/hooks/usePermissions";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, AlertTriangle, Eye, FileText } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { handleRpcError } from "@/lib/rpcErrors";
import { ExportToolbar } from "@/components/shared/ExportToolbar";
import { exportPdf } from "@/lib/export";
import { InvoicePrintTemplate } from "@/components/shared/print-templates/InvoicePrintTemplate";
import { useIsMobile } from "@/hooks/use-mobile";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  total: number;
  status: string;
  customer_id: string | null;
  customer_visible?: boolean;
  customers?: { name: string } | null;
}

interface BillableLine {
  sovLineId: string;
  description: string;
  units: string[];
  confirmedTotal: number;
  unconfirmedTotal: number;
  status: "confirmed" | "unconfirmed" | "mixed";
}

interface BillableExpense {
  id: string;
  description: string;
  amount: number;
  category: string;
}

interface Props {
  projectId: string;
  invoices: Invoice[];
  customers: { id: string; name: string }[];
  contractValue: number;
  onMutate: () => void;
  project?: any;
}

export const ProjectInvoicesTab = ({ projectId, invoices, customers, contractValue, onMutate, project }: Props) => {
  const isMobile = useIsMobile();
  const { can, isAdmin } = usePermissions();
  const { data: companyProfile } = useCompanyProfile();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState({ invoice_number: "", invoice_date: "", customer_id: "" });
  const [notes, setNotes] = useState("");
  const [billableLines, setBillableLines] = useState<BillableLine[]>([]);
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [billableExpenses, setBillableExpenses] = useState<BillableExpense[]>([]);
  const [selectedExpenses, setSelectedExpenses] = useState<Set<string>>(new Set());
  const [loadingLines, setLoadingLines] = useState(false);

  const canManageVisibility = isAdmin || can("manage_customers");

  const fetchBillableLines = async () => {
    setLoadingLines(true);

    // Fetch bill lines
    const { data: billLines } = await supabase
      .from("bill_lines")
      .select("id, description, quantity, unit_price, line_total, sov_line_id, sov_lines(description), bill_id, bills!inner(status, project_id)")
      .eq("bills.project_id", projectId);

    const grouped: Record<string, BillableLine> = {};
    for (const bl of (billLines ?? []) as any[]) {
      const billStatus = bl.bills?.status;
      if (!billStatus) continue;
      const sovId = bl.sov_line_id ?? bl.id;
      const sovDesc = bl.sov_lines?.description ?? bl.description;
      const isConfirmed = ["approved", "paid", "partially_paid"].includes(billStatus);
      const isPending = ["draft", "pending", "submitted"].includes(billStatus);
      if (!isConfirmed && !isPending) continue;
      const unitMatch = bl.description?.match(/(?:Unit|unit|#)\s*(\S+)/);
      const unitNum = unitMatch?.[1];
      if (!grouped[sovId]) {
        grouped[sovId] = { sovLineId: sovId, description: sovDesc, units: unitNum ? [unitNum] : [], confirmedTotal: 0, unconfirmedTotal: 0, status: isConfirmed ? "confirmed" : "unconfirmed" };
      }
      if (unitNum && !grouped[sovId].units.includes(unitNum)) grouped[sovId].units.push(unitNum);
      if (isConfirmed) grouped[sovId].confirmedTotal += Number(bl.line_total);
      else grouped[sovId].unconfirmedTotal += Number(bl.line_total);
    }
    for (const g of Object.values(grouped)) {
      if (g.confirmedTotal > 0 && g.unconfirmedTotal > 0) g.status = "mixed";
      else if (g.confirmedTotal > 0) g.status = "confirmed";
      else g.status = "unconfirmed";
    }
    const lines = Object.values(grouped);
    setBillableLines(lines);
    setSelectedLines(new Set(lines.filter((l) => l.status === "confirmed" || l.status === "mixed").map((l) => l.sovLineId)));

    // Fetch billable expenses
    const { data: expenses } = await supabase
      .from("project_expenses")
      .select("id, description, amount, category")
      .eq("project_id", projectId)
      .eq("status", "approved");

    const expList: BillableExpense[] = (expenses ?? []).map((e: any) => ({
      id: e.id, description: e.description ?? e.category, amount: Number(e.amount), category: e.category ?? "expense",
    }));
    setBillableExpenses(expList);
    setSelectedExpenses(new Set(expList.map((e) => e.id)));

    setLoadingLines(false);
  };

  const openDialog = () => {
    setForm({ invoice_number: "", invoice_date: format(new Date(), "yyyy-MM-dd"), customer_id: project?.customer_id ?? "" });
    setSelectedLines(new Set());
    setSelectedExpenses(new Set());
    setBillableLines([]);
    setBillableExpenses([]);
    setNotes("");
    setOpen(true);
    fetchBillableLines();
  };

  const toggleLine = (id: string) => setSelectedLines((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleExpense = (id: string) => setSelectedExpenses((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const selectAllLines = () => {
    const allIds = billableLines.map((l) => l.sovLineId);
    setSelectedLines((prev) => prev.size === allIds.length ? new Set() : new Set(allIds));
  };
  const selectAllExpenses = () => {
    const allIds = billableExpenses.map((e) => e.id);
    setSelectedExpenses((prev) => prev.size === allIds.length ? new Set() : new Set(allIds));
  };

  const selectedLineTotal = useMemo(() => billableLines.filter((l) => selectedLines.has(l.sovLineId)).reduce((s, l) => s + l.confirmedTotal + l.unconfirmedTotal, 0), [billableLines, selectedLines]);
  const selectedExpenseTotal = useMemo(() => billableExpenses.filter((e) => selectedExpenses.has(e.id)).reduce((s, e) => s + e.amount, 0), [billableExpenses, selectedExpenses]);
  const selectedTotal = selectedLineTotal + selectedExpenseTotal;

  const billedStatuses = ["approved", "sent", "paid", "partially_paid", "overdue"];
  const totalBilled = invoices.filter((i) => billedStatuses.includes(i.status)).reduce((s, i) => s + Number(i.total), 0);
  const totalPending = invoices.filter((i) => i.status === "draft").reduce((s, i) => s + Number(i.total), 0);
  const totalPaid = invoices.reduce((s, i) => s + Number((i as any).amount_paid || 0), 0);
  const overbilled = contractValue > 0 && totalBilled > contractValue;

  const toggleCustomerVisible = async (invId: string, current: boolean) => {
    const { error } = await supabase.from("invoices").update({ customer_visible: !current }).eq("id", invId);
    if (error) return toast.error(error.message);
    toast.success(!current ? "Shared with customer" : "Hidden from customer");
    onMutate();
  };

  const approve = async (id: string) => {
    setBusy(id);
    const { error } = await supabase.rpc("approve_invoice", { p_invoice_id: id });
    setBusy(null);
    if (error) return handleRpcError(error);
    toast.success("Invoice approved");
    onMutate();
  };

  const buildPreviewData = () => {
    const selLines = billableLines.filter((l) => selectedLines.has(l.sovLineId));
    const selExps = billableExpenses.filter((e) => selectedExpenses.has(e.id));
    const lineItems = selLines.map((l) => ({
      description: l.units.length > 0 ? `${l.description} · Unit${l.units.length > 1 ? "s" : ""} ${l.units.join(", ")}` : l.description,
      quantity: 1, unit_price: l.confirmedTotal + l.unconfirmedTotal, line_total: l.confirmedTotal + l.unconfirmedTotal,
    }));
    const expenseItems = selExps.map((e) => ({
      description: `${e.category.replace(/_/g, " ")} — ${e.description}`, quantity: 1, unit_price: e.amount, line_total: e.amount,
    }));
    return {
      invoice_number: form.invoice_number || "DRAFT",
      invoice_date: form.invoice_date,
      due_date: null,
      status: "draft",
      total: selectedTotal,
      amount_paid: 0,
      customer_name: customers.find((c) => c.id === form.customer_id)?.name,
      project_name: project?.name,
      company_name: companyProfile?.name ?? project?.gc_name,
      company_logo_url: companyProfile?.logo_url,
      line_items: lineItems,
      expenses: expenseItems,
      notes: notes || undefined,
    };
  };

  const preview = () => {
    if (selectedLines.size === 0 && selectedExpenses.size === 0) return toast.error("Select at least one item");
    exportPdf(<InvoicePrintTemplate data={buildPreviewData()} />, `Invoice Preview`);
  };

  const create = async () => {
    if (!form.invoice_date) return toast.error("Date required");
    if (selectedLines.size === 0 && selectedExpenses.size === 0) return toast.error("Select at least one item");
    setBusy("new");

    const { data: companyId } = await supabase.rpc("my_company_id");
    const { data: inv, error } = await supabase.from("invoices").insert({
      project_id: projectId, company_id: companyId!, invoice_number: form.invoice_number || null,
      invoice_date: form.invoice_date, customer_id: form.customer_id || null, notes: notes || null,
      subtotal: selectedTotal, total: selectedTotal,
    }).select("id").single();

    if (error) { setBusy(null); return toast.error(error.message); }

    // Build all line items
    const allItems: any[] = [];
    const selLines = billableLines.filter((l) => selectedLines.has(l.sovLineId));
    for (const l of selLines) {
      allItems.push({
        invoice_id: inv!.id, company_id: companyId!,
        description: l.units.length > 0 ? `${l.description} · Unit${l.units.length > 1 ? "s" : ""} ${l.units.join(", ")}` : l.description,
        quantity: 1, unit_price: l.confirmedTotal + l.unconfirmedTotal,
      });
    }
    const selExps = billableExpenses.filter((e) => selectedExpenses.has(e.id));
    for (const e of selExps) {
      allItems.push({
        invoice_id: inv!.id, company_id: companyId!,
        description: `${e.category.replace(/_/g, " ")} — ${e.description}`,
        quantity: 1, unit_price: e.amount,
      });
    }
    if (allItems.length > 0) {
      const { error: lineError } = await supabase.from("invoice_lines").insert(allItems);
      if (lineError) toast.error("Invoice created but line items failed: " + lineError.message);
    }

    setBusy(null);
    toast.success("Invoice created");
    setOpen(false);
    onMutate();
  };

  return (
    <div className="space-y-4">
      {overbilled && (
        <Alert className="bg-destructive/10 border-destructive/30">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <AlertDescription className="text-destructive">
            Overbilling alert: Total billed ({fmt(totalBilled)}) exceeds contract value ({fmt(contractValue)}).
          </AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        {invoices.length > 0 && <ExportToolbar entityType="invoices" data={invoices} />}
        {(isAdmin || can("create_invoices")) && (
          <Button size="sm" onClick={openDialog}><Plus className="mr-1 h-4 w-4" />New Invoice</Button>
        )}
      </div>

      {!invoices.length ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground mb-2">No invoices yet</p>
          <Button variant="outline" size="sm" onClick={openDialog}>Create your first Invoice</Button>
        </div>
      ) : (
        <>
          {isMobile ? (
            /* ── Mobile Card View ── */
            <div className="space-y-3">
              {invoices.map((inv) => (
                <div key={inv.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-sm">{inv.invoice_number || "—"}</span>
                    <StatusBadge status={inv.status} />
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {inv.invoice_date ? format(new Date(inv.invoice_date), "MMM d, yyyy") : "—"}
                    {inv.customers?.name && <span> · {inv.customers.name}</span>}
                  </div>
                  <div className="text-lg font-bold">{fmt(Number(inv.total))}</div>
                  <div className="flex items-center gap-2 pt-1">
                    {canManageVisibility && (
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Eye className="h-3.5 w-3.5" />
                        <Switch checked={!!inv.customer_visible} onCheckedChange={() => toggleCustomerVisible(inv.id, !!inv.customer_visible)} />
                      </div>
                    )}
                    <div className="flex-1" />
                    {inv.status === "draft" && can("approve_invoices") && (
                      <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" disabled={busy === inv.id} onClick={() => approve(inv.id)}>Approve</Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* ── Desktop Table View ── */
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  {canManageVisibility && <TableHead className="text-center"><Eye className="h-4 w-4 mx-auto" /></TableHead>}
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoices.map((inv, i) => (
                  <TableRow key={inv.id} className={i % 2 === 1 ? "bg-muted/30" : ""}>
                    <TableCell className="font-medium">{inv.invoice_number || "—"}</TableCell>
                    <TableCell>{inv.invoice_date ? format(new Date(inv.invoice_date), "MMM d, yyyy") : "—"}</TableCell>
                    <TableCell>{inv.customers?.name ?? "—"}</TableCell>
                    <TableCell className="text-right">{fmt(Number(inv.total))}</TableCell>
                    <TableCell><StatusBadge status={inv.status} /></TableCell>
                    {canManageVisibility && (
                      <TableCell className="text-center">
                        <Switch checked={!!inv.customer_visible} onCheckedChange={() => toggleCustomerVisible(inv.id, !!inv.customer_visible)} className="mx-auto" />
                      </TableCell>
                    )}
                    <TableCell className="text-right">
                      {inv.status === "draft" && can("approve_invoices") && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 h-7 text-xs" disabled={busy === inv.id} onClick={() => approve(inv.id)}>Approve</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <div className="flex flex-wrap justify-between gap-2 text-sm text-muted-foreground">
            <span>Pending: <span className="font-semibold text-foreground">{fmt(totalPending)}</span></span>
            <span>Billed: <span className="font-semibold text-foreground">{fmt(totalBilled)}</span></span>
            <span>Collected: <span className="font-semibold text-foreground">{fmt(totalPaid)}</span></span>
          </div>
        </>
      )}

      {/* New Invoice Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Invoice</DialogTitle>
            <DialogDescription>Create an invoice from approved bill line items and expenses.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Header fields */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Invoice Number <span className="text-muted-foreground text-xs">(optional)</span></Label>
                <Input value={form.invoice_number} onChange={(e) => setForm((p) => ({ ...p, invoice_number: e.target.value }))} placeholder="Leave blank for QB sync" />
              </div>
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={form.invoice_date} onChange={(e) => setForm((p) => ({ ...p, invoice_date: e.target.value }))} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Customer</Label>
              <Input value={customers.find((c) => c.id === form.customer_id)?.name ?? ""} readOnly className="bg-muted" />
            </div>

            {/* Billable SOV Lines */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Billable Line Items</Label>
                {billableLines.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllLines}>
                    {selectedLines.size === billableLines.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
              </div>
              {loadingLines ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading billable items…</p>
              ) : billableLines.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No billable items found.</p>
              ) : (
                <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                  {billableLines.map((line) => (
                    <div key={line.sovLineId} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer" onClick={() => toggleLine(line.sovLineId)}>
                      <Checkbox checked={selectedLines.has(line.sovLineId)} onCheckedChange={() => toggleLine(line.sovLineId)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {line.description}
                          {line.units.length > 0 && <span className="text-muted-foreground font-normal"> · Unit{line.units.length > 1 ? "s" : ""} {line.units.join(", ")}</span>}
                        </p>
                        <div className="flex gap-2 mt-0.5">
                          {line.confirmedTotal > 0 && <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300 dark:border-green-800">Confirmed {fmt(line.confirmedTotal)}</Badge>}
                          {line.unconfirmedTotal > 0 && <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300 dark:border-amber-800">Unconfirmed {fmt(line.unconfirmedTotal)}</Badge>}
                        </div>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">{fmt(line.confirmedTotal + line.unconfirmedTotal)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Billable Expenses */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Billable Expenses & Reimbursements</Label>
                {billableExpenses.length > 0 && (
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={selectAllExpenses}>
                    {selectedExpenses.size === billableExpenses.length ? "Deselect All" : "Select All"}
                  </Button>
                )}
              </div>
              {loadingLines ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Loading…</p>
              ) : billableExpenses.length === 0 ? (
                <p className="text-sm text-muted-foreground py-4 text-center">No billable expenses found.</p>
              ) : (
                <div className="border rounded-md divide-y max-h-40 overflow-y-auto">
                  {billableExpenses.map((exp) => (
                    <div key={exp.id} className="flex items-center gap-3 px-3 py-2 hover:bg-muted/50 cursor-pointer" onClick={() => toggleExpense(exp.id)}>
                      <Checkbox checked={selectedExpenses.has(exp.id)} onCheckedChange={() => toggleExpense(exp.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{exp.description}</p>
                        <Badge variant="secondary" className="text-xs mt-0.5">{exp.category.replace(/_/g, " ")}</Badge>
                      </div>
                      <span className="text-sm font-semibold whitespace-nowrap">{fmt(exp.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes <span className="text-muted-foreground text-xs">(appears on invoice)</span></Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Payment terms, thank you message, special instructions…" rows={3} />
            </div>

            {/* Totals */}
            {(selectedLines.size > 0 || selectedExpenses.size > 0) && (
              <div className="border-t pt-3 space-y-1">
                {selectedLineTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Services ({selectedLines.size} items)</span>
                    <span className="font-medium">{fmt(selectedLineTotal)}</span>
                  </div>
                )}
                {selectedExpenseTotal > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Expenses ({selectedExpenses.size} items)</span>
                    <span className="font-medium">{fmt(selectedExpenseTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between text-base font-bold pt-1 border-t">
                  <span>Invoice Total</span>
                  <span>{fmt(selectedTotal)}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="secondary" onClick={preview} disabled={selectedLines.size === 0 && selectedExpenses.size === 0}>
              <FileText className="mr-1 h-4 w-4" />Preview
            </Button>
            <Button disabled={busy === "new" || (selectedLines.size === 0 && selectedExpenses.size === 0)} onClick={create}>
              {busy === "new" ? "Saving…" : "Create Invoice"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
